/**
 * Active navigation link updater.
 *
 * Drupal's menu block assigns `is-active` to the current link
 * server-side, but after a client-side SPA navigation the menu
 * HTML reflects the old page until the next full render. This
 * helper sweeps the document and applies the active state based
 * on the target URL, matching how Drupal core does it.
 *
 * Scope is intentionally limited to `is-active` on the anchor and
 * `menu-item--active-trail` on its ancestor `<li>`, because those
 * are the two classes every Drupal theme expects and the narrow
 * contract keeps the helper predictable.
 */

export function updateActiveLinks(targetUrl: string): void {
  const currentPath = normalizePath(extractPath(targetUrl));

  document.querySelectorAll<HTMLAnchorElement>('a[href]').forEach((link) => {
    const href = link.getAttribute('href') ?? '';
    if (!isInternalNavPath(href)) {
      return;
    }
    const linkPath = normalizePath(stripQueryAndHash(href));
    const isActive = linkPath === currentPath;

    link.classList.toggle('is-active', isActive);
    const parentItem = link.closest('li');
    if (parentItem) {
      parentItem.classList.toggle('menu-item--active-trail', isActive);
    }
  });
}

function extractPath(url: string): string {
  try {
    return new URL(url, location.origin).pathname;
  }
  catch {
    return location.pathname;
  }
}

function normalizePath(path: string): string {
  if (path !== '/' && path.endsWith('/')) {
    return path.slice(0, -1);
  }
  return path;
}

function stripQueryAndHash(href: string): string {
  const [pathAndMaybeHash] = href.split('?');
  return pathAndMaybeHash.split('#')[0];
}

function isInternalNavPath(href: string): boolean {
  return !href.startsWith('http') && !href.startsWith('#') && !href.startsWith('javascript');
}
